import './home.scss'
import { useContext, useEffect, useRef, useState } from 'react';
import Cardview from './Cardview';
import SummaryList from '../summaryList/Summarylist';
import FilterCriteriaDrawer from './FilterCriteriaDrawer';
import { Autocomplete, Badge, Box, Button, CircularProgress, FormControl, IconButton, TextField, Tooltip, useTheme } from '@mui/material';
import userProfileServices from '../../services/api-services/user-profile-api.service'
import { useSnackbar } from '../../context/SnackbarContext';
import { useUser } from '../../context/UserContext';
import { LoadingContext } from '../../App';
// import '../../components/common.scss';
import { useParams, useSearchParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AiResults from './AiResults';
import { currentToken, filterSkillOptions } from './searchQuery';

// Classic keyword search is hidden for now: running it alongside AI search
// gave the page two boxes with two different chip behaviors. The code paths
// below stay in place — they are still the degraded mode we fall back to when
// the AI search is unavailable. Flip this to re-expose the engine toggle and
// honour ?engine=classic.
const CLASSIC_ENGINE_ENABLED = false;

const formValues = {
    firstName: "",
    lastName: "",
    bio: "",
    organization_id: [],
    location: "",
    skillName: [],
    organizationsName: []
}

const Home = (props: any) => {
    const { companyDetail, displayFilterOption } = props;
    const theme = useTheme();
    const { id } = useParams<{ id: any }>();
    const { loading, setLoading } = useContext(LoadingContext);
    const { fetchUserProfileList } = userProfileServices()
    const showSnackbar = useSnackbar();

    const [view, setView] = useState('cardView');
    const { isLoggedOut, user, organizationsList, skillsList = [], suggestedSkills = [], skillsLoaded = false } = useUser();
    const [profiles, setProfiles] = useState<any>([])
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const itemsPerPage = 100;
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isActiveFilter, setIsActiveFilter] = useState<boolean>(false);
    const [filterFormValues, setFilterFormValues] = useState<any>({
        ...formValues,
        organization_id: (id || companyDetail?.id) ? [id || companyDetail?.id] : [],

    })
    const debounceRef = useRef<any>(null);

    const [engine, setEngine] = useState<'ai' | 'classic'>('ai');
    const [aiQuery, setAiQuery] = useState('');
    const [searchId, setSearchId] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [fallbackNotice, setFallbackNotice] = useState('');
    const [skillsOpen, setSkillsOpen] = useState(false);
    const [aiMeta, setAiMeta] = useState<any>(null);
    const [totalProfiles, setTotalProfiles] = useState<number | null>(null);
    const searchWrapRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    // a classic query that arrived (bookmark / hard load) before the skills vocabulary did
    const pendingClassicQuery = useRef<string | null>(null);

    // close the all-skills dropdown on outside click
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) { setSkillsOpen(false); }
        };
        if (skillsOpen) { document.addEventListener('mousedown', onDown); }
        return () => document.removeEventListener('mousedown', onDown);
    }, [skillsOpen])


    useEffect(() => {
        const { firstName, lastName, bio, location, skillName, organization_id } = filterFormValues;
        if (firstName || lastName || bio || location || skillName?.length > 0 || organization_id?.length > 0) {
            setIsActiveFilter(true)
        } else {
            setIsActiveFilter(false)
        }
    }, [filterFormValues])

    useEffect(() => {
        getUserProfileActiveList(filterFormValues);
    }, [id, currentPage, user])


    useEffect(() => {
        if (typeof isLoggedOut !== 'undefined' && isLoggedOut) {
            if (currentPage == 1) {
                getUserProfileActiveList(filterFormValues)
            } else {
                setCurrentPage(1)
            }
            // if (currentPage != 1) { return setCurrentPage(1) }
            // if (currentPage == 1) { getUserProfileActiveList(filterFormValues) }
        }
    }, [isLoggedOut])


    const loadMore = () => {
        setCurrentPage(prevPage => prevPage + 1);
    };

    const onSearch = (formValues: any) => {
        if ((id || companyDetail?.id) && !formValues?.organization_id?.includes(id || companyDetail?.id)) { formValues?.organization_id.push(id || companyDetail?.id) }
        setDrawerOpen(false)
        setFilterFormValues(formValues)
        if (currentPage > 1) return setCurrentPage(1)
        getUserProfileActiveList(formValues)
    }

    const debouncedSearch = (updatedFilters: any, delay: number = 800) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            onSearch(updatedFilters);
        }, delay);
    };

    const getUserProfileActiveList = async (formValues: any) => {
        try {
            setLoading(true);
            const playload = { "filterCriteria": { ...formValues } }

            const resProfiles = await fetchUserProfileList(currentPage, itemsPerPage, playload)

            if (resProfiles?.data?.length > 0) {
                currentPage === 1 ? setProfiles(resProfiles?.data) : setProfiles((pre: any) => ([...pre, ...resProfiles?.data]))
                setHasMore(currentPage < resProfiles?.pagination?.totalPages);
                setTotalProfiles(resProfiles?.pagination?.totalItems ?? resProfiles?.data?.length ?? null);
            }
            else {
                setProfiles([])
                setTotalProfiles(0)
            }
            setLoading(false);

        }
        catch (error: any) {
            setProfiles([]);
            setLoading(false);
            showSnackbar(error?.message || 'An error occurred while getting profiles list', 'error');
        }
    }


    const buttonSx = {
        ...(loading && {
            bgcolor: theme.palette.primary.main,
        }),
    }

    const autoHandleChange = (e: any, value: any) => {
        const updatedFilters = { ...filterFormValues, skillName: value };
        setFilterFormValues(updatedFilters);
        debouncedSearch(updatedFilters);
    };

    const handleRemoveFilter = (key: string, value?: string) => {
        setFilterFormValues((prev: any) => {
            const updatedFilters = Array.isArray(prev[key])
                ? { ...prev, [key]: prev[key]?.filter((item: any) => item !== value) }
                : { ...prev, [key]: '' };

            debouncedSearch(updatedFilters);
            return updatedFilters;
        });
    };

    const resetFilters = () => {
        setFilterFormValues(formValues)
        debouncedSearch(formValues);
    }

    // Searches navigate: the URL is the one code path that triggers a search, so
    // typing+Enter, chip clicks, back/forward, and bookmarked links all behave the
    // same. Each search pushes a history entry — that's what makes Back work.
    const pushSearch = (q: string, nextEngine: 'ai' | 'classic' = 'ai') => {
        const trimmed = q.trim();
        if (!trimmed) { return; }
        setFallbackNotice('');
        setSearchParams(nextEngine === 'classic' ? { q: trimmed, engine: 'classic' } : { q: trimmed });
    };

    const runAiSearch = () => pushSearch(aiQuery);

    // The X in the box. Clearing a query that has been searched is a navigation
    // back to the empty URL, so the [searchParams] sync below tears the results
    // down the same way Back does and the landing list returns. A query that was
    // only typed (never searched) has nothing in the URL — just empty the box.
    const clearSearch = () => {
        setAiQuery('');
        setSkillsOpen(false);
        setFallbackNotice('');
        if (searchParams.get('q')) { setSearchParams({}); }
        searchInputRef.current?.focus();
    };

    // Map the free-text query onto the classic engine: known tags found in the
    // query become skillName filters; otherwise the raw text searches bios.
    const runClassicFromQuery = (query: string = aiQuery) => {
        const q = query.toLowerCase();
        const matchedSkills = (skillsList || []).filter((s: string) => q.includes(s.toLowerCase()));
        const mapped = {
            ...formValues,
            organization_id: filterFormValues?.organization_id || [],
            skillName: matchedSkills,
            bio: matchedSkills.length ? '' : query.trim(),
        };
        setFilterFormValues(mapped);
        if (currentPage > 1) { setCurrentPage(1); } else { getUserProfileActiveList(mapped); }
    };

    const switchEngine = (next: 'ai' | 'classic') => {
        if (!aiQuery.trim()) { setEngine(next); return; }
        pushSearch(aiQuery, next);
    };

    // The single URL → state sync: runs on mount (bookmarks), on our own
    // pushSearch navigations, and on browser back/forward. An empty q is the
    // idle landing state.
    useEffect(() => {
        const q = (searchParams.get('q') || '').trim();
        const engineParam = CLASSIC_ENGINE_ENABLED && searchParams.get('engine') === 'classic' ? 'classic' : 'ai';
        setAiQuery(q);
        if (!q) { setSearchId(0); return; }
        setEngine(engineParam);
        if (engineParam === 'ai') {
            setSearchId((n) => n + 1);
        } else if (skillsLoaded) {
            runClassicFromQuery(q);
        } else {
            // mapping needs the vocabulary; run it once the skills fetch settles
            pendingClassicQuery.current = q;
        }
    }, [searchParams]);

    useEffect(() => {
        if (!skillsLoaded || pendingClassicQuery.current === null) { return; }
        const q = pendingClassicQuery.current;
        pendingClassicQuery.current = null;
        runClassicFromQuery(q);
    }, [skillsLoaded]);

    const handleAiUnavailable = (message?: string) => {
        setFallbackNotice(message || 'AI search is unavailable right now — showing keyword results.');
        setEngine('classic');
        runClassicFromQuery();
    };

    // Picking a skill — from the suggested chips or from the caret dropdown —
    // REPLACES the query and searches. Stacking picks used to splice the skill
    // into the text, which mashed overlapping skills together ("Bioinformatics
    // pipeline development" + "Package development" -> "Bioinformatics pipeline
    // Package development"). Multi-keyword queries are typed, not clicked.
    const pickSkill = (skill: string) => {
        if (!skill) { return; }
        setSkillsOpen(false);
        // set the box directly as well as navigating: re-picking the skill that is
        // already in the URL writes an identical query, and an identical query is
        // a no-op navigation — the [searchParams] sync below never re-fires and
        // the box would keep whatever the user had typed over it.
        setAiQuery(skill);
        pushSearch(skill);
    }


    return (
        <>
            <div className='mt-4 mb-1'>
                <div className="ai-searchwrap" ref={searchWrapRef}>
                    {engine === 'classic' ? (
                    // Classic keyword = the production tag picker: selected skills sit in
                    // the box as removable chips. The AI box only makes sense for free text.
                    <FormControl fullWidth>
                        <Autocomplete
                            className='w-100'
                            multiple
                            options={skillsList}
                            value={filterFormValues?.skillName || []}
                            onChange={autoHandleChange}
                            renderInput={(params) => (
                                <TextField {...params} label="Search by keyword" name="search" />
                            )}
                        />
                    </FormControl>
                    ) : (<>
                    <div className="ai-searchbox">
                        <TextField
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            inputProps={{ 'aria-label': 'Search members and facilities', enterKeyHint: 'search' }}
                            placeholder="Ask anything or type a keyword"
                            inputRef={searchInputRef}
                            value={aiQuery}
                            onChange={(e) => setAiQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { setSkillsOpen(false); runAiSearch(); }
                                if (e.key === 'Escape') { setSkillsOpen(false); }
                            }}
                        />
                        {aiQuery !== '' && (
                            <button
                                type="button"
                                className="ai-clear"
                                aria-label="Clear search"
                                title="Clear search"
                                onClick={clearSearch}
                            ><CloseIcon /></button>
                        )}
                        <span className="ai-powered-badge" title="AI-powered search">
                            <span className="spark">✦</span><span className="txt">AI-powered</span>
                        </span>
                        {/* same indicator as the classic Autocomplete's popup button, so the two boxes read as one */}
                        <button
                            type="button"
                            className={`ai-caret${skillsOpen ? ' open' : ''}`}
                            aria-label="Browse all skills"
                            aria-expanded={skillsOpen}
                            onClick={() => setSkillsOpen((o) => !o)}
                        ><ArrowDropDownIcon /></button>
                        {skillsOpen && (() => {
                        // typing filters the open list by the token being typed (combobox behavior)
                        const token = currentToken(aiQuery);
                        const options = filterSkillOptions(skillsList || [], aiQuery);
                        const visible = options.length ? options : (skillsList || []);
                        return (
                            <div className="ai-skillsdrop" role="listbox" aria-label="All skills">
                                {options.length === 0 && (
                                    <div className="ai-skillempty">No skills match “{token}” — showing all</div>
                                )}
                                {visible.map((skill: string) => (
                                    <button key={skill} type="button" role="option"
                                        className="ai-skillopt"
                                        onClick={() => pickSkill(skill)}>
                                        {skill}
                                    </button>
                                ))}
                            </div>
                        );
                        })()}
                    </div>
                    </>)}
                </div>
                {suggestedSkills?.length > 0 && (
                    <div className="suggested-tag-wrapper mb-3">
                        <label className="text-muted me-2 small-font">Suggested searches:</label>
                        <div className="ai-chiprow">
                            {suggestedSkills?.map((skill: string) => {
                                // a chip reads as active while it is the query it would set
                                const isActive = aiQuery.trim().toLowerCase() === skill.toLowerCase();
                                return (
                                    <button
                                        key={skill}
                                        type="button"
                                        className={`suggested-tag rounded-pill ${isActive ? 'active' : ''}`}
                                        onClick={() => pickSkill(skill)}
                                    >
                                        {skill}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}
                <div className="mb-3 skill-tag-wrapper">
                    <div className='d-flex align-items-center flex-wrap'>
                        {[...Object.entries(filterFormValues)?.flatMap(([key, value]) => {
                            // If value string
                            if (typeof value === "string" && value.trim() !== "") {
                                return [{
                                    key,
                                    label: `${key?.toLowerCase()}: ${value}`,
                                    onRemove: () => handleRemoveFilter(key),
                                }];
                            }

                            // for orfanaization filer for display org Name
                            if (!companyDetail && key === "organization_id" && Array.isArray(value)) {
                                return value.map((orgId: string) => {
                                    const matchedOrg = organizationsList?.find((org: any) => org?.id === orgId);
                                    if (!matchedOrg) return null;
                                    return {
                                        key: orgId,
                                        label: `Org: ${matchedOrg.organizationName}`,
                                        onRemove: () => handleRemoveFilter(key, orgId),
                                    };
                                }).filter(Boolean);
                            }

                            return [];
                        })
                        ]?.map((item: any) => (
                            <div
                                key={item.key}
                                className="rounded-pill tags d-flex align-items-center px-2 py-1 bg-light text-muted me-2 mb-1"
                            >
                                <label className="me-1 text-capitalize">{item.label}</label>
                                <IconButton size="small" onClick={item.onRemove}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </div>
                        ))}

                        {/* If value found in object than display else not*/}
                        {Object.entries(filterFormValues)?.some(([key, value]) =>
                            !companyDetail && key !== 'skillName' && (Array.isArray(value) && value?.length > 0) || (typeof value === 'string' && value?.trim() !== '')
                        ) && (
                                <label className='text-primary cursor-pointer' onClick={() => resetFilters()} >
                                    Clear filters
                                </label>
                            )}
                    </div>
                </div>
            </div>


            {(searchId > 0 || engine === 'classic') && (
                <>
                    <div className="ai-viewbar">
                        <span className="rq">
                            {engine === 'ai' && aiMeta && (
                                <><b>{aiMeta.count} results</b> · {aiMeta.members} members · {aiMeta.facilities} facilities</>
                            )}
                            {engine === 'classic' && totalProfiles !== null && (
                                <b>{totalProfiles} results</b>
                            )}
                        </span>
                        {CLASSIC_ENGINE_ENABLED && (
                            <div className="seg" role="tablist" aria-label="Search engine">
                                <button aria-pressed={engine === 'ai'} onClick={() => switchEngine('ai')}>✦ AI search</button>
                                <button aria-pressed={engine === 'classic'} onClick={() => switchEngine('classic')}>Classic keyword</button>
                            </div>
                        )}
                    </div>
                </>
            )}
            {fallbackNotice && <div className="ai-notice">{fallbackNotice}</div>}

            {engine === 'ai' && searchId > 0 && (
                <AiResults query={aiQuery} searchId={searchId} onUnavailable={handleAiUnavailable} onMeta={setAiMeta} />
            )}

            {/* Grid/List and Filter drive the classic profile list. With classic
                hidden the landing list is the grid and AI results are the list,
                so the toolbar goes with the engine toggle. */}
            {CLASSIC_ENGINE_ENABLED && (
            <div className='d-flex justify-content-end gap-1 text-muted'>
                {profiles?.length > 0 && (<>
                    {view == 'summaryList' && (
                        <Tooltip title="Card View" arrow>
                            <div className='cursor-pointer' onClick={() => setView('cardView')}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="24px" fill="#6c757d "><path d="M120-520v-320h320v320H120Zm0 400v-320h320v320H120Zm400-400v-320h320v320H520Zm0 400v-320h320v320H520ZM200-600h160v-160H200v160Zm400 0h160v-160H600v160Zm0 400h160v-160H600v160Zm-400 0h160v-160H200v160Zm400-400Zm0 240Zm-240 0Zm0-240Z" /></svg>
                            </div>
                        </Tooltip>
                    )}
                    {!companyDetail && view == 'cardView' && (<>
                        <Tooltip title="List View" arrow>
                            <div className='cursor-pointer' onClick={() => setView('summaryList')}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#6c757d "><path d="M120-200v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Z" /></svg>
                            </div>
                        </Tooltip>
                        <span className='border'></span>
                    </>)}
                </>)}

                <Tooltip title="Filter" arrow>

                    <div className='cursor-pointer' onClick={() => setDrawerOpen(true)}>
                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="M440-160q-17 0-28.5-11.5T400-200v-240L168-736q-15-20-4.5-42t36.5-22h560q26 0 36.5 22t-4.5 42L560-440v240q0 17-11.5 28.5T520-160h-80Zm40-308 198-252H282l198 252Zm0 0Z" /></svg>

                        {isActiveFilter && (<Badge color="primary" variant="dot" style={{ position: 'relative', top: '-8px', right: '4px' }}></Badge>)}
                    </div>

                </Tooltip>
            </div>
            )}
            {drawerOpen && (
                <FilterCriteriaDrawer
                    filterFormValues={filterFormValues}
                    onSearch={onSearch}
                    organizations={organizationsList}
                    onClose={() => setDrawerOpen(false)}
                    displayFilterOption={displayFilterOption}
                />)}

            {!(engine === 'ai' && searchId > 0) && (
                profiles?.length > 0 ? (<>

                    {view == 'cardView' && (<Cardview profiles={profiles} />)}
                    {view == 'summaryList' && (<SummaryList profiles={profiles} />)}

                    <div className='d-flex justify-content-center mt-2 mb-5'>
                        {hasMore && (
                            <Box sx={{ m: 1, position: 'relative' }}>
                                <Button color='primary' variant="contained" sx={buttonSx} disabled={loading} onClick={() => loadMore()}>
                                    Load More Profiles
                                </Button>
                                {loading && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            color: theme.palette.primary.main,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            marginTop: '-12px',
                                            marginLeft: '-12px',
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </div>
                </>
                ) : <>
                    <p className="text-muted">{loading ? 'Fetching profiles...' : 'No profiles found to display.'}</p>
                </>
            )}
        </>
    )
}
export default Home
